import type { AssistantActivity } from '@secretario/shared';
import { skillObject } from '../../lib/areas.js';

/**
 * O que o Secretário mostra enquanto resolve um pedido: a fala durante
 * ("Conferindo sua agenda"), um detalhe curto, a fala depois ("Conferi sua
 * agenda") e o objeto 3D de cada tarefa. Vale para o microfone e para a conversa.
 */
export type AssistantStep = { label: string; detail: string; done: string; art: string };

const STEPS: Record<string, AssistantStep> = {
  consultar_agenda: {
    label: 'Conferindo sua agenda',
    detail: 'Olhando compromissos e horários livres.',
    done: 'Conferi sua agenda',
    art: '/digest-icons/agenda-compromisso.webp',
  },
  criar_compromisso: {
    label: 'Marcando na agenda',
    detail: 'Guardando dia, hora e com quem.',
    done: 'Marquei na agenda',
    art: '/digest-icons/agenda-compromisso.webp',
  },
  criar_lembrete: {
    label: 'Criando o lembrete',
    detail: 'Eu te aviso na hora certa.',
    done: 'Criei o lembrete',
    art: '/digest-icons/agenda-lembrete.webp',
  },
  buscar_contatos: {
    label: 'Procurando nos seus contatos',
    detail: 'Buscando a pessoa certa.',
    done: 'Achei nos seus contatos',
    art: '/digest-icons/contato-novo.webp',
  },
  preparar_mensagem: {
    label: 'Preparando a mensagem',
    detail: 'Você confere antes de enviar.',
    done: 'Deixei a mensagem pronta',
    art: '/digest-icons/contato-novo.webp',
  },
  consultar_memoria: {
    label: 'Consultando o Sobre você',
    detail: 'Trazendo só o que ajuda agora.',
    done: 'Lembrei do que você contou',
    art: '/owner-facts/voce.webp',
  },
  guardar_na_memoria: {
    label: 'Anotando sobre você',
    detail: 'Fica visível em Sobre você.',
    done: 'Anotei sobre você',
    art: '/owner-facts/voce.webp',
  },
  previsao_do_tempo: {
    label: 'Conferindo o tempo',
    detail: 'Conferindo a previsão do dia certo.',
    done: 'Vi a previsão',
    art: skillObject('clima'),
  },
  resumo_do_dia: {
    label: 'Montando o seu dia',
    detail: 'Reunindo o que importa hoje.',
    done: 'Montei o seu dia',
    art: skillObject('bom_dia'),
  },
  pensar_com_calma: {
    label: 'Pensando com calma',
    detail: 'Comparando com cuidado antes de responder.',
    done: 'Pensei com calma',
    art: '/digest-icons/topico-curtido.webp',
  },
  pesquisar_na_internet: {
    label: 'Pesquisando na internet',
    detail: 'Conferindo fontes confiáveis.',
    done: 'Pesquisei na internet',
    art: '/digest-icons/topico-curtido.webp',
  },
  consultar_indicadores: {
    label: 'Conferindo no Banco Central',
    detail: 'O valor oficial, com a data.',
    done: 'Conferi no Banco Central',
    art: skillObject('mercado'),
  },
  consultar_feriados: {
    label: 'Olhando os feriados',
    detail: 'Os nacionais e as emendas.',
    done: 'Olhei os feriados',
    art: skillObject('datas_importantes'),
  },
  consultar_cep: {
    label: 'Procurando o CEP',
    detail: 'Rua, bairro e cidade.',
    done: 'Achei o CEP',
    art: skillObject('passagens'),
  },
  consultar_empresa: {
    label: 'Consultando o CNPJ',
    detail: 'Os dados públicos da empresa.',
    done: 'Consultei o CNPJ',
    art: skillObject('negocio'),
  },
  preparar_saida: {
    label: 'Preparando a saída',
    detail: 'Rota e corrida com o destino.',
    done: 'Saída pronta na tela',
    art: skillObject('passagens'),
  },
  preparar_ligacao: {
    label: 'Preparando a ligação',
    detail: 'O número do contato.',
    done: 'Ligação pronta na tela',
    art: skillObject('passagens'),
  },
  ler_anexo: {
    label: 'Lendo o que você mandou',
    detail: 'A foto ou o arquivo, do jeito que veio.',
    done: 'Li o que você mandou',
    art: '/menu/entregas.webp',
  },
  guardar_anexo: {
    label: 'Guardando em Documentos',
    detail: 'Fica em Guardados, para achar depois.',
    done: 'Guardado em Documentos',
    art: '/menu/entregas.webp',
  },
  anotar_ganho: {
    label: 'Anotando o ganho',
    detail: 'Valor, data e onde ele entra.',
    done: 'Anotei no Dinheiro',
    art: skillObject('contas'),
  },
  guardar_relatorio: {
    label: 'Guardando em Documentos',
    detail: 'O relatório da tela, com gráfico e fontes.',
    done: 'Guardado em Documentos',
    art: '/menu/entregas.webp',
  },
  mudar_ganho: {
    label: 'Corrigindo o ganho',
    detail: 'Valor, data ou nome, como você pediu.',
    done: 'Ganho corrigido',
    art: skillObject('contas'),
  },
  mudar_conta: {
    label: 'Corrigindo a conta',
    detail: 'Valor, vencimento ou nome, como você pediu.',
    done: 'Conta corrigida',
    art: skillObject('contas'),
  },
  marcar_ganho_recebido: {
    label: 'Dando entrada no ganho',
    detail: 'O que caiu e quanto.',
    done: 'Entrada anotada',
    art: skillObject('contas'),
  },
  resumo_do_dinheiro: {
    label: 'Fazendo as contas do mês',
    detail: 'O que entrou, o que saiu e a sobra.',
    done: 'Contas do mês feitas',
    art: skillObject('contas'),
  },
  abrir_idiomas: {
    label: 'Abrindo a aula',
    detail: 'A aula do dia, no seu nível.',
    done: 'Aula aberta',
    art: skillObject('noticias'),
  },
  progresso_no_idioma: {
    label: 'Conferindo o progresso',
    detail: 'Nível, aulas, palavras suas e o ritmo.',
    done: 'Progresso conferido',
    art: skillObject('noticias'),
  },
  procurar_tela: {
    label: 'Procurando a tela',
    detail: 'A TV ou o notebook que você ligou.',
    done: 'Tela encontrada',
    art: '/menu/ajustes.webp',
  },
  conectar_tela_grande: {
    label: 'Conectando à tela',
    detail: 'Preparando a exibição.',
    done: 'Mostrando na tela',
    art: '/menu/ajustes.webp',
  },
  tirar_da_tela: {
    label: 'Tirando da tela',
    detail: 'A tela volta ao descanso.',
    done: 'Tela liberada',
    art: '/menu/ajustes.webp',
  },
  tv_buscar: {
    label: 'Buscando a TV',
    detail: 'Escolha a TV na tela do celular.',
    done: 'TV encontrada',
    art: '/menu/ajustes.webp',
  },
  tv_conectar: {
    label: 'Conectando à TV',
    detail: 'Conectando à sua tela.',
    done: 'Mostrando na TV',
    art: '/menu/ajustes.webp',
  },
  preparar_apresentacao: {
    label: 'Montando a apresentação',
    detail: 'Estrutura, slides e a fala de cada um.',
    done: 'Apresentação a caminho da tela',
    art: skillObject('mercado'),
  },
  controlar_apresentacao: {
    label: 'Passando o slide',
    detail: 'Em exibição.',
    done: 'Slide na tela',
    art: '/menu/ajustes.webp',
  },
  abrir_planos: {
    label: 'Abrindo os planos',
    detail: 'Para você comparar com calma.',
    done: 'Planos abertos',
    art: '/menu/ajustes.webp',
  },
  controlar_video: {
    label: 'Controlando o vídeo',
    detail: 'Na sua tela.',
    done: 'Feito',
    art: '/menu/ajustes.webp',
  },
  apresentacao: {
    label: 'Montando a apresentação',
    detail: 'Slide a slide.',
    done: 'Pronto',
    art: skillObject('mercado'),
  },
  preparar_relatorio_visual: {
    label: 'Montando o relatório',
    detail: 'Pesquisa, números e o gráfico.',
    done: 'Relatório a caminho da tela',
    art: skillObject('mercado'),
  },
  preparar_resposta: {
    label: 'Preparando a resposta',
    detail: 'Juntando o que achei para te contar.',
    done: 'Resposta pronta',
    art: '/digest-icons/topico-curtido.webp',
  },
  procurar_documento: {
    label: 'Procurando nos seus documentos',
    detail: 'Receitas, documentos, atas e o que você guardou.',
    done: 'Achei nos seus documentos',
    art: '/menu/entregas.webp',
  },
  consultar_especialista: {
    label: 'Falando com a equipe',
    detail: 'Vendo o que os especialistas separaram.',
    done: 'Falei com a equipe',
    art: skillObject('bom_dia'),
  },
};

export function stepOf(activity: AssistantActivity): AssistantStep {
  return (
    STEPS[activity.tool] ?? {
      label: activity.label,
      detail: 'Cuidando disso para você.',
      done: activity.label,
      art: '/digest-icons/topico-curtido.webp',
    }
  );
}

/** Etapa pelo nome da ferramenta (conversa ao vivo, onde só o nome chega antes do resultado). */
export function stepOfTool(tool: string): AssistantStep {
  return (
    STEPS[tool] ?? {
      label: 'Cuidando disso',
      detail: 'Já te conto.',
      done: 'Pronto',
      art: '/digest-icons/topico-curtido.webp',
    }
  );
}
